import { useMemo } from 'react';
import { useVideoCall } from './useVideoCall.js';
import { useVideoPreferences } from './useVideoPreferences.js';

/**
 * Return type for the visibility hook
 */
interface VideoOverlayVisibility {
  /** Whether to show the video overlay at all */
  shouldShowOverlay: boolean;

  /** Whether to show the local participant's "no video" placeholder */
  shouldShowLocalPlaceholder: boolean;

  /** Whether to show error UI */
  shouldShowError: boolean;

  /** Whether to show reconnecting UI */
  shouldShowReconnecting: boolean;

  /**
   * Whether to show the first-connection notice.
   *
   * True from entering a private area until the room is up: the token
   * round-trip allows 10s and the LiveKit connect another 15s, and without
   * this the UI says nothing at all for up to 25 seconds (#80).
   */
  shouldShowConnecting: boolean;

  /** Whether the overlay should be in minimal mode (no participants with video) */
  isMinimalMode: boolean;

  /** Reason why overlay is hidden (for debugging) */
  hiddenReason?: string;
}

/**
 * Hook to determine video overlay visibility based on current state
 *
 * This centralizes all the complex visibility logic in one place,
 * making it easier to understand and modify the rules as requirements change.
 *
 * @returns {VideoOverlayVisibility} Object containing visibility flags
 *
 * @example
 * ```tsx
 * function VideoOverlay() {
 *   const { shouldShowOverlay, shouldShowLocalPlaceholder } = useVideoOverlayVisibility();
 *
 *   if (!shouldShowOverlay) return null;
 *
 *   return <VideoOverlayContent showPlaceholder={shouldShowLocalPlaceholder} />;
 * }
 * ```
 */
export function useVideoOverlayVisibility(): VideoOverlayVisibility {
  const {
    isConnected,
    room,
    currentPrivateArea,
    cameraEnabled,
    micEnabled,
    canControl,
    participants,
    error,
    isReconnecting,
    connectionAttempts
  } = useVideoCall();

  // `autoJoin` is not part of the video context; it lives with the other
  // stored media preferences, and it is the third thing that can start a
  // connection on its own.
  const { preferences: { autoJoin } } = useVideoPreferences();

  return useMemo(() => {
    // Error state takes precedence
    if (error && !isConnected && currentPrivateArea) {
      return {
        shouldShowOverlay: false,
        shouldShowLocalPlaceholder: false,
        shouldShowError: true,
        shouldShowReconnecting: false,
        shouldShowConnecting: false,
        isMinimalMode: false,
        hiddenReason: 'error'
      };
    }

    // Reconnecting state
    if (isReconnecting && !error) {
      return {
        shouldShowOverlay: false,
        shouldShowLocalPlaceholder: false,
        shouldShowError: false,
        shouldShowReconnecting: true,
        shouldShowConnecting: false,
        isMinimalMode: false,
        hiddenReason: 'reconnecting'
      };
    }

    // Not in a private area
    if (!currentPrivateArea) {
      return {
        shouldShowOverlay: false,
        shouldShowLocalPlaceholder: false,
        shouldShowError: false,
        shouldShowReconnecting: false,
        shouldShowConnecting: false,
        isMinimalMode: false,
        hiddenReason: 'not_in_private_area'
      };
    }

    // Not connected to video room. This is the window the connecting notice
    // covers — but only when a connection is actually being attempted. The
    // provider auto-connects for an available player who wants some media or
    // has auto-join on; for anybody else "Connecting..." would be a lie that
    // never resolves.
    if (!isConnected || !room) {
      const connectionExpected = canControl && (micEnabled || cameraEnabled || autoJoin);

      return {
        shouldShowOverlay: false,
        shouldShowLocalPlaceholder: false,
        shouldShowError: false,
        shouldShowReconnecting: false,
        shouldShowConnecting: connectionExpected,
        isMinimalMode: false,
        hiddenReason: connectionExpected ? 'connecting' : 'not_connected'
      };
    }

    // Connected but alone with camera off - don't show anything
    if (!cameraEnabled && participants === 0) {
      return {
        shouldShowOverlay: false,
        shouldShowLocalPlaceholder: false,
        shouldShowError: false,
        shouldShowReconnecting: false,
        shouldShowConnecting: false,
        isMinimalMode: false,
        hiddenReason: 'alone_camera_off'
      };
    }

    // Show overlay - determine if we need local placeholder
    const shouldShowLocalPlaceholder = !cameraEnabled && participants > 0;

    return {
      shouldShowOverlay: true,
      shouldShowLocalPlaceholder,
      shouldShowError: false,
      shouldShowReconnecting: false,
      shouldShowConnecting: false,
      isMinimalMode: !cameraEnabled && participants === 0,
      hiddenReason: undefined
    };
  }, [
    isConnected,
    room,
    currentPrivateArea,
    cameraEnabled,
    micEnabled,
    canControl,
    autoJoin,
    participants,
    error,
    isReconnecting,
    connectionAttempts
  ]);
}